import React, { useEffect, useState } from "react"
import DropdownMenu from "./commons/DropdownMenu"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "../context/authContext"
import { useAtom } from "jotai"
import { cartListAtom, showCartAtom, showMessageBoxAtom, showNotificationsBoxAtom, userAtom } from "../store"
import DialogConfirmation from "./dialogs/DialogConfirmation"
import { listenToUnreadMessageCount, getUnreadMessageCount, testAddMessage } from "../firebase/messages"
import { listenToUnreadNotifications, getUnreadNotifications, testAddNotification } from "../firebase/notifications"
import { addNotificationsToExistingSales, addNotificationForSale, addNotificationsForSuccessfulCheckout } from "../firebase/sales"
import { addNotificationsToExistingOffers } from "../firebase/offers"
import { testEmailNotification, testEmailNotificationClear, testOfferNotificationEmail, testEmailJSContactFormat, testContactTemplateNotification, testExactContactFormat, checkEmailJSTemplate, testFirebaseCloudFunctionEmail, checkUserEmail } from "../firebase/emailNotifications"
import { collection, query, where, getDocs, updateDoc } from "firebase/firestore"
import { db } from "../firebase/config"

const Header = ({ isLogin = false }) => {
  const [userData, setUserData] = useAtom(userAtom)
  const [showMessageBox, setShowMessageBox] = useAtom(showMessageBoxAtom)
  const [showNotificationsBox, setShowNotificationsBox] = useAtom(showNotificationsBoxAtom)
  
  // Debug the notifications box state
  useEffect(() => {
    // Only log when state actually changes to true
    if (showNotificationsBox) {
      console.log('=== NOTIFICATIONS BOX OPENED ===');
    }
  }, [showNotificationsBox]);
  const [showCart, setShowCart] = useAtom(showCartAtom)
  const [cartList, setCartList] = useAtom(cartListAtom)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [unreadMessages, setUnreadMessages] = useState([])
  const [unreadNotifications, setUnreadNotifications] = useState([])
  const { logout, user } = useAuth()
  const [isFixed, setIsFixed] = useState(false);
  const navigate = useNavigate()
  const location = useLocation()
  const isActive = (path) => location.pathname.includes(path)
  const isExactActive = (path) => location.pathname === path
  const [showMembershipRequiredModal, setShowMembershipRequiredModal] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsFixed(true);
      } else {
        setIsFixed(false);
      }
    };

    const handleClick = () => {
      setShowProfileMenu(false)
    }

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClick);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Real-time unread message count listener
  useEffect(() => {
    if (user && user.uid) {
      try {
        console.log('Setting up real-time message listener for user:', user.uid);
        console.log('Current userData.unReadMessages:', userData?.unReadMessages?.length || 0);
        
        // Get initial count first
        getUnreadMessageCount(user.uid).then((initialMessages) => {
          console.log('Initial unread messages from messages collection:', initialMessages.length);
          setUnreadMessages(initialMessages);
        }).catch(error => {
          console.error('Error getting initial message count:', error);
        });
        
        // Set up real-time listener
        const unsubscribe = listenToUnreadMessageCount(user.uid, (messages) => {
          try {
                    // Only log significant changes
        if (messages.length > 0) {
          console.log('=== REAL-TIME MESSAGE UPDATE ===');
          console.log('New message count:', messages.length);
        }
            
                    // Ensure we have the latest count by getting unique messages
        const uniqueMessages = messages.filter((msg, index, self) => 
          index === self.findIndex(m => m.id === msg.id)
        );
        
        // Only log if count actually changed
        if (uniqueMessages.length !== unreadMessages.length) {
          console.log('Message count changed:', uniqueMessages.length);
        }
        setUnreadMessages(uniqueMessages);
          } catch (error) {
            console.error('Error in real-time message count update:', error);
            // Don't fallback to manual refresh to avoid conflicts
          }
        });

        // Cleanup function
        return () => {
          console.log('Cleaning up real-time message listener');
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        };
      } catch (error) {
        console.error('Error setting up message listener:', error);
        // Don't fallback to manual refresh to avoid conflicts
      }
    }
  }, [user]); // Removed userData dependency to prevent re-initialization

  // Real-time unread notifications listener
  useEffect(() => {
    if (user && user.uid) {
      try {
        console.log('Setting up real-time notifications listener for user:', user.uid);
        
        // Get initial notifications count first
        getUnreadNotifications(user.uid).then((initialNotifications) => {
          console.log('Initial unread notifications:', initialNotifications.length);
          setUnreadNotifications(initialNotifications);
        }).catch(error => {
          console.error('Error getting initial notifications count:', error);
        });
        
        // Set up real-time listener
        const unsubscribe = listenToUnreadNotifications(user.uid, (notifications) => {
          try {
            console.log('=== REAL-TIME NOTIFICATIONS COUNT UPDATE ===');
            console.log('Received notifications from listener:', notifications);
            console.log('Notifications count:', notifications.length);
            
            // Ensure we have the latest count by getting unique notifications
            const uniqueNotifications = notifications.filter((notif, index, self) => 
              index === self.findIndex(n => n.id === notif.id)
            );
            
            console.log('Unique notifications count:', uniqueNotifications.length);
            setUnreadNotifications(uniqueNotifications);
          } catch (error) {
            console.error('Error in real-time notifications count update:', error);
          }
        });

        // Cleanup function
        return () => {
          console.log('Cleaning up real-time notifications listener');
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        };
      } catch (error) {
        console.error('Error setting up notifications listener:', error);
      }
    }
  }, [user]);

  const onLogout = () => {
    logout()
    navigate('/login')
  }

  const handleCreateListing = () => {
    if (userData.membership === 'free') {
      setShowMembershipRequiredModal(true)
    } else {
      navigate(`/marketplace/create-listing`)
    }
  }

  const handleClick = () => {
    // if (location.pathname === '/support'){
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // }
  }

  const refreshMessageCount = async () => {
    if (user && user.uid) {
      console.log('Manually refreshing message count...');
      try {
        const messages = await getUnreadMessageCount(user.uid);
        console.log('Refreshed message count:', messages.length);
        setUnreadMessages(messages);
      } catch (error) {
        console.error('Error refreshing message count:', error);
      }
    }
  }

  const testAddMessage = async () => {
    if (user && user.uid) {
      console.log('Adding test message...');
      try {
        await testAddMessage(user.uid);
        console.log('Test message added successfully');
      } catch (error) {
        console.error('Error adding test message:', error);
      }
    }
  }

  const testAddNotification = async () => {
    if (user && user.uid) {
      console.log('Adding test notification...');
      try {
        const { testAddNotification: addTestNotification } = await import('../firebase/notifications');
        await addTestNotification(user.uid);
        console.log('Test notification added successfully');
      } catch (error) {
        console.error('Error adding test notification:', error);
      }
    }
  }

  const debugNotifications = async () => {
    if (user && user.uid) {
      console.log('=== DEBUGGING NOTIFICATIONS ===');
      console.log('User ID:', user.uid);
      
      try {
        const notifications = await getUnreadNotifications(user.uid);
        console.log('Unread notifications found:', notifications);
        console.log('Notifications count:', notifications.length);
        
        // Also check all notifications for this user
        const allNotifications = await getUnreadNotifications(user.uid);
        console.log('All notifications for user:', allNotifications);
      } catch (error) {
        console.error('Error debugging notifications:', error);
      }
    }
  }

  const debugMessageCount = () => {
    console.log('=== DEBUG MESSAGE COUNT ===');
    console.log('Current unread messages:', unreadMessages);
    console.log('User data unread messages:', userData?.unReadMessages);
    console.log('Total unread count:', unreadMessages.length);
    
    // Group messages by sender
    const messagesBySender = {};
    unreadMessages.forEach(msg => {
      const senderId = msg.senderId || msg.sender;
      if (!messagesBySender[senderId]) {
        messagesBySender[senderId] = [];
      }
      messagesBySender[senderId].push(msg);
    });
    
    console.log('Messages by sender:', messagesBySender);
  }

  const debugNotificationCount = () => {
    console.log('=== DEBUG NOTIFICATION COUNT ===');
    console.log('Current unread notifications:', unreadNotifications);
    console.log('Total unread notifications:', unreadNotifications.length);
    
    // Group notifications by type
    const notificationsByType = {};
    unreadNotifications.forEach(notif => {
      const type = notif.type || 'unknown';
      if (!notificationsByType[type]) {
        notificationsByType[type] = [];
      }
      notificationsByType[type].push(notif);
    });
    
    console.log('Notifications by type:', notificationsByType);
  }

  const addNotificationsToExistingSalesTest = async () => {
    if (user && user.uid) {
      console.log('=== ADDING NOTIFICATIONS TO EXISTING SALES ===');
      try {
        const count = await addNotificationsToExistingSales();
        console.log(`Successfully added notifications to ${count} sales`);
        alert(`Added notifications to ${count} existing sales!`);
      } catch (error) {
        console.error('Error adding notifications to existing sales:', error);
        alert('Error adding notifications to existing sales');
      }
    }
  }

  const testAddNotificationForSale = async () => {
    if (user && user.uid) {
      console.log('=== TESTING ADD NOTIFICATION FOR SALE ===');
      // You can replace this with an actual sale ID from your database
      const testSaleId = prompt('Enter a sale ID to test notifications:');
      if (testSaleId) {
        try {
          const success = await addNotificationForSale(testSaleId);
          if (success) {
            console.log('✅ Successfully added notifications for sale:', testSaleId);
            alert('✅ Successfully added notifications for sale!');
          } else {
            console.log('❌ Failed to add notifications for sale:', testSaleId);
            alert('❌ Failed to add notifications for sale');
          }
        } catch (error) {
          console.error('Error adding notification for sale:', error);
          alert('Error adding notification for sale');
        }
      }
    }
  }

  const testSuccessfulCheckoutNotification = async () => {
    if (user && user.uid) {
      console.log('=== TESTING SUCCESSFUL CHECKOUT NOTIFICATION ===');
      
      // Mock sale data based on your structure
      const mockSaleData = {
        cardId: "im5hS7fwUZ6w4iSiRdki",
        category: "2diyTecx3pvksBlbkCgL",
        count: 1,
        listing_title: "Test6",
        price: 100,
        receiver: "qYIGsdvBURfYD5M09qdwWqyvGm63",
        sender: "kSpmUDi3vhS4ggGHRvGk5rg1oVU2"
      };
      
      try {
        const success = await addNotificationsForSuccessfulCheckout(mockSaleData);
        if (success) {
          console.log('✅ Successfully added notifications for successful checkout');
          alert('✅ Successfully added notifications for successful checkout!');
        } else {
          console.log('❌ Failed to add notifications for successful checkout');
          alert('❌ Failed to add notifications for successful checkout');
        }
      } catch (error) {
        console.error('Error adding notifications for successful checkout:', error);
        alert('Error adding notifications for successful checkout');
      }
    }
  }

  const addNotificationsToRecentSales = async () => {
    if (user && user.uid) {
      console.log('=== ADDING NOTIFICATIONS TO RECENT SALES ===');
      try {
        // Get recent sales from the last 24 hours
        const salesRef = collection(db, "sales");
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        const q = query(salesRef, where("created_at", ">=", yesterday));
        const querySnapshot = await getDocs(q);
        
        let count = 0;
        for (const doc of querySnapshot.docs) {
          const saleData = { id: doc.id, ...doc.data() };
          
          // Skip if already has notifications
          if (saleData.notifications_sent) continue;
          
          try {
            console.log('Processing recent sale:', saleData);
            
            // Send notification to seller
            const sellerNotificationData = {
              type: 'purchase_completed',
              action: 'purchased',
              listingId: saleData.cardId,
              listingTitle: saleData.listing_title || saleData.title,
              recipientId: saleData.receiver,
              message: `Your listing "${saleData.listing_title || saleData.title}" has been purchased for $${saleData.price.toFixed(2)}.`,
              timestamp: new Date(),
              isRead: false
            };
            
            await addNotification(sellerNotificationData);
            
            // Send notification to buyer
            const buyerNotificationData = {
              type: 'purchase_completed',
              action: 'purchased',
              listingId: saleData.cardId,
              listingTitle: saleData.listing_title || saleData.title,
              recipientId: saleData.sender,
              message: `You have successfully purchased "${saleData.listing_title || saleData.title}" for $${saleData.price.toFixed(2)}.`,
              timestamp: new Date(),
              isRead: false
            };
            
            await addNotification(buyerNotificationData);
            
            // Mark as processed
            await updateDoc(doc.ref, { notifications_sent: true });
            count++;
            
            console.log(`✅ Added notifications for recent sale ${saleData.id}`);
          } catch (error) {
            console.error(`❌ Error adding notifications for recent sale ${saleData.id}:`, error);
          }
        }
        
        console.log(`✅ Added notifications to ${count} recent sales`);
        alert(`✅ Added notifications to ${count} recent sales!`);
        return count;
      } catch (error) {
        console.error('❌ Error adding notifications to recent sales:', error);
        alert('❌ Error adding notifications to recent sales');
      }
    }
  }

  const addNotificationsToExistingOffersTest = async () => {
    if (user && user.uid) {
      console.log('=== ADDING NOTIFICATIONS TO EXISTING OFFERS ===');
      try {
        const count = await addNotificationsToExistingOffers();
        console.log(`✅ Successfully added notifications to ${count} offers`);
        alert(`✅ Added notifications to ${count} existing offers!`);
      } catch (error) {
        console.error('❌ Error adding notifications to existing offers:', error);
        alert('❌ Error adding notifications to existing offers');
      }
    }
  }

  const testEmailNotificationFunction = async () => {
    try {
      console.log('=== TESTING EMAIL NOTIFICATION ===');
      if (!user || !user.uid) {
        alert('No user logged in');
        return;
      }
      
      console.log('Testing email for user:', user.uid);
      console.log('User email:', user.email);
      
      // First check if user has email in profile
      const emailCheck = await checkUserEmail(user.uid);
      console.log('Email check result:', emailCheck);
      
      if (!emailCheck.hasEmail) {
        alert('❌ No email found in user profile. Please update your profile with an email address.');
        return;
      }
      
      alert(`📧 Email found: ${emailCheck.email}\n\nSending test email...`);
      
      const success = await testEmailNotificationClear(user.uid);
      if (success) {
        alert('✅ Test email notification sent successfully! Check your email.');
      } else {
        alert('❌ Test email notification failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error testing email notification:', error);
      alert('Error testing email notification');
    }
  }

  const testOfferEmailFunction = async () => {
    try {
      if (!user || !user.uid) {
        alert('No user logged in');
        return;
      }
      
      const emailCheck = await checkUserEmail(user.uid);
      if (!emailCheck.hasEmail) {
        alert('❌ No email found in user profile. Please update your profile with an email address.');
        return;
      }
      
      alert(`📧 Testing offer notification email to: ${emailCheck.email}`);
      
      const success = await testOfferNotificationEmail(user.uid);
      if (success) {
        alert('✅ Offer notification email sent successfully! Check your email.');
      } else {
        alert('❌ Offer notification email failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error testing offer email notification:', error);
      alert('Error testing offer email notification');
    }
  }

  const testContactFormatEmail = async () => {
    try {
      if (!user || !user.uid) {
        alert('No user logged in');
        return;
      }
      
      const emailCheck = await checkUserEmail(user.uid);
      if (!emailCheck.hasEmail) {
        alert('❌ No email found in user profile. Please update your profile with an email address.');
        return;
      }
      
      alert(`📧 Testing Contact format email to: ${emailCheck.email}`);
      
      const success = await testEmailJSContactFormat(user.uid);
      if (success) {
        alert('✅ Contact format email sent successfully! Check your email.');
      } else {
        alert('❌ Contact format email failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error testing Contact format email:', error);
      alert('Error testing Contact format email');
    }
  }

  const testContactTemplateNotification = async () => {
    try {
      if (!user || !user.uid) {
        alert('No user logged in');
        return;
      }
      
      const emailCheck = await checkUserEmail(user.uid);
      if (!emailCheck.hasEmail) {
        alert('❌ No email found in user profile. Please update your profile with an email address.');
        return;
      }
      
      alert(`📧 Testing Contact template notification to: ${emailCheck.email}`);
      
      const success = await testContactTemplateNotification(user.uid);
      if (success) {
        alert('✅ Contact template notification sent successfully! Check your email.');
      } else {
        alert('❌ Contact template notification failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error testing Contact template notification:', error);
      alert('Error testing Contact template notification');
    }
  }

  const testExactContactFormatFunction = async () => {
    try {
      if (!user || !user.uid) {
        alert('No user logged in');
        return;
      }
      
      const emailCheck = await checkUserEmail(user.uid);
      if (!emailCheck.hasEmail) {
        alert('❌ No email found in user profile. Please update your profile with an email address.');
        return;
      }
      
      alert(`📧 Testing EXACT Contact format to: ${emailCheck.email}`);
      
      const success = await testExactContactFormat(user.uid);
      if (success) {
        alert('✅ Exact Contact format test successful! Check your email.');
      } else {
        alert('❌ Exact Contact format test failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error testing exact Contact format:', error);
      alert('Error testing exact Contact format');
    }
  }

  const checkEmailJSTemplateFunction = async () => {
    try {
      alert('🔧 Checking EmailJS template configuration...');
      
      const success = await checkEmailJSTemplate();
      if (success) {
        alert('✅ EmailJS template configuration is working!');
      } else {
        alert('❌ EmailJS template configuration has issues. Check console for details.');
      }
    } catch (error) {
      console.error('Error checking EmailJS template:', error);
      alert('Error checking EmailJS template');
    }
  }

  const testFirebaseCloudFunctionEmailFunction = async () => {
    try {
      if (!user || !user.uid) {
        alert('No user logged in');
        return;
      }
      
      const emailCheck = await checkUserEmail(user.uid);
      if (!emailCheck.hasEmail) {
        alert('❌ No email found in user profile. Please update your profile with an email address.');
        return;
      }
      
      alert(`📧 Testing Firebase Cloud Function email to: ${emailCheck.email}`);
      
      const success = await testFirebaseCloudFunctionEmail(user.uid);
      if (success) {
        alert('✅ Firebase Cloud Function email sent successfully! Check your email.');
      } else {
        alert('❌ Firebase Cloud Function email failed. Check console for details.');
      }
    } catch (error) {
      console.error('Error testing Firebase Cloud Function email:', error);
      alert('Error testing Firebase Cloud Function email');
    }
  }

  // Debug effect to track message count changes
  useEffect(() => {
    console.log('=== MESSAGE COUNT CHANGED ===');
    console.log('New count:', unreadMessages.length);
    console.log('Messages:', unreadMessages);
  }, [unreadMessages]);



  return (
    <>
      <DialogConfirmation
        message="Selling requires a membership. Buying is free -- you only need an account."
        open={showMembershipRequiredModal}
        onClose={() => { setShowMembershipRequiredModal(false) }}
        buttonText="View Plans"
        title="Seller Membership Required"
        iconSrc="/assets/logos/logo.svg"
        iconClassName="w-[160px]"
        onButtonClick={() => { navigate('/my-profile'), setShowMembershipRequiredModal(false) }}
        type="confirm"
      />

      <div className={`px-4 py-4 w-full z-[100] h-fit  fixed border-b-[1px] border-[#474747] bg-[#181B1C] left-0 top-0`}>
        <div className="flex px-4 container mx-auto items-center justify-between w-full">
          <div className="flex items-center lg:hidden  gap-6">
            <DropdownMenu />
          </div>

          <div className="flex items-center">
            <Link to="/" onClick={handleClick}>
              <img
                className="h-7 lg:min-h-[2.5rem]"
                src="/assets/logos/logo.svg"
                alt=""
              />
            </Link>

            <div className="splitter xl:mx-9 mx-7 h-5 max-lg:hidden" />

            <div className="hidden lg:flex gap-[2rem] text-[1rem] uppercase  text-white ">
              <div className="flex items-center text-[#A0A0A0] hover:text-[#484F52]">
                <Link to="/" onClick={handleClick} className={isExactActive('/') ? 'text-primary font-bold' : ''}>Home</Link>
              </div>
              <div className="flex items-center text-[#A0A0A0] hover:text-[#484F52]">
                <Link to="/marketplace/categories" onClick={handleClick} className={isActive('/marketplace/categories') ? 'text-primary font-bold' : ''}>Find cards</Link>
              </div>

              <div className="flex items-center text-[#A0A0A0] hover:text-[#484F52]">
                <Link to="/support" onClick={handleClick} className={isActive('/support') ? 'text-primary font-bold' : ''}>Support</Link>
              </div>

              <div className="flex items-center text-[#A0A0A0] hover:text-[#484F52]">
                <Link
                  to="/plans"
                  onClick={handleClick}
                  className={isActive("/plans") ? "text-primary font-bold" : ""}
                >
                  Plans
                </Link>
              </div>

              <div className="flex items-center text-[#A0A0A0] hover:text-[#484F52]">
                <Link to="/faqs" onClick={handleClick} className={isActive('/faqs') ? 'text-primary font-bold' : ''}>FAQs</Link>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {!userData ?
              <Link to="/login" className="hover:opacity-50">
                <button className="flex items-center text-white">
                  <img
                    src={"/assets/icons/icon-person.svg"}
                    alt="icon"
                    className="mr-2"
                  />
                  LOGIN
                </button>
              </Link>
              :
              <>
                <div className="relative">
                  <div 
                  className="flex items-center relative cursor-pointer" 
                  onClick={(e) => { e.stopPropagation(), setShowProfileMenu(!showProfileMenu) }}
                  onAuxClick={addNotificationsToRecentSales}
                >
                    <div className="ml-4 flex items-center whitespace-nowrap max-lg:hidden">
                      <div className="w-8 h-8 mr-2 flex items-center justify-center  rounded-[50%] border-[2px]">
                        <img src={userData.picture} alt="user" className="w-full h-full rounded-[50%]" />
                        {/* <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_763_2198)">
                            <path d="M8.89618 10.0862C11.0863 10.0862 12.8617 8.31076 12.8617 6.12067C12.8617 3.93057 11.0863 2.15515 8.89618 2.15515C6.70609 2.15515 4.93066 3.93057 4.93066 6.12067C4.93066 8.31076 6.70609 10.0862 8.89618 10.0862Z" stroke="#1AB6F9" strokeWidth="1.18966" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2.94824 13.556C4.14843 11.4822 6.32823 10.0862 8.89652 10.0862C11.4648 10.0862 13.6446 11.4822 14.8448 13.556" stroke="#1AB6F9" strokeWidth="1.18966" strokeLinecap="round" strokeLinejoin="round" />
                          </g>
                          <defs>
                            <clipPath id="clip0_763_2198">
                              <rect width="15.8621" height="15.8621" fill="white" transform="translate(0.96582 0.172424)" />
                            </clipPath>
                          </defs>
                        </svg> */}
                      </div>

                      <div>
                        <div className="text-base font-medium text-white">
                          {userData.displayName}
                        </div>
                      </div>

                      <img
                        src="/assets/icons/icon-down-white.svg"
                        className="rotate-[180deg] ml-2"
                      />
                    </div>

                    <div className="lg:hidden mt-2">
                      <button>
                        <img src="/assets/icons/icon-user-default.svg" alt="user" />
                      </button>
                    </div>
                  </div>

                  <div className={`absolute top-[48px] w-[200px] bg-white max-lg:-right-[20px] ${!showProfileMenu ? 'hidden' : ''}`}>
                    <Link
                      to="/my-profile"
                      className="block hover:text-primary py-1 px-3 border-b-[1px] border-gray-200">
                      Profile
                    </Link>

                    <button
                      onClick={handleCreateListing}
                      className="block hover:text-primary py-1 px-3 border-b-[1px] border-gray-200">
                      Create Listing
                    </button>

                    <Link to="/faqs" className="block hover:text-primary py-1 px-3 border-b-[1px] border-gray-200">
                      FAQ's
                    </Link>

                    <button
                      className="block hover:text-primary py-1 px-3 border-b-[1px]"
                      onClick={onLogout}
                    >
                      Log out
                    </button>
                  </div>
                </div>

                <button 
                  className="relative max-lg:hidden" 
                  onClick={() => setShowMessageBox(true)}
                  onDoubleClick={testFirebaseCloudFunctionEmailFunction}
                  onContextMenu={debugMessageCount}
                  onAuxClick={checkEmailJSTemplateFunction}
                >
                  <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <mask id="mask0_767_3793" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="23" height="23">
                      <rect width="22.3284" height="22.3284" fill="white" />
                    </mask>
                    <g mask="url(#mask0_767_3793)">
                      <path d="M7.44406 10.2814C7.67184 10.2814 7.86598 10.2012 8.02646 10.0409C8.18679 9.88042 8.26696 9.68629 8.26696 9.45851C8.26696 9.23073 8.18679 9.0366 8.02646 8.87611C7.86598 8.71578 7.67184 8.63561 7.44406 8.63561C7.21628 8.63561 7.02215 8.71578 6.86167 8.87611C6.70134 9.0366 6.62117 9.23073 6.62117 9.45851C6.62117 9.68629 6.70134 9.88042 6.86167 10.0409C7.02215 10.2012 7.21628 10.2814 7.44406 10.2814ZM11.1655 10.2814C11.3932 10.2814 11.5874 10.2012 11.7479 10.0409C11.9082 9.88042 11.9884 9.68629 11.9884 9.45851C11.9884 9.23073 11.9082 9.0366 11.7479 8.87611C11.5874 8.71578 11.3932 8.63561 11.1655 8.63561C10.9377 8.63561 10.7435 8.71578 10.5831 8.87611C10.4227 9.0366 10.3426 9.23073 10.3426 9.45851C10.3426 9.68629 10.4227 9.88042 10.5831 10.0409C10.7435 10.2012 10.9377 10.2814 11.1655 10.2814ZM14.8869 10.2814C15.1146 10.2814 15.3088 10.2012 15.4692 10.0409C15.6296 9.88042 15.7097 9.68629 15.7097 9.45851C15.7097 9.23073 15.6296 9.0366 15.4692 8.87611C15.3088 8.71578 15.1146 8.63561 14.8869 8.63561C14.6591 8.63561 14.4649 8.71578 14.3045 8.87611C14.1441 9.0366 14.064 9.23073 14.064 9.45851C14.064 9.68629 14.1441 9.88042 14.3045 10.0409C14.4649 10.2012 14.6591 10.2814 14.8869 10.2814ZM2.32715 19.7282V4.16273C2.32715 3.69275 2.48996 3.29495 2.81558 2.96933C3.1412 2.64371 3.539 2.4809 4.00899 2.4809H18.3219C18.7919 2.4809 19.1897 2.64371 19.5153 2.96933C19.841 3.29495 20.0038 3.69275 20.0038 4.16273V14.7543C20.0038 15.2243 19.841 15.6221 19.5153 15.9477C19.1897 16.2733 18.7919 16.4361 18.3219 16.4361H5.61919L2.32715 19.7282ZM5.02516 15.0406H18.3219C18.3936 15.0406 18.4592 15.0107 18.5187 14.9511C18.5784 14.8915 18.6082 14.8259 18.6082 14.7543V4.16273C18.6082 4.0911 18.5784 4.02551 18.5187 3.96596C18.4592 3.90627 18.3936 3.87642 18.3219 3.87642H4.00899C3.93735 3.87642 3.87176 3.90627 3.81222 3.96596C3.75252 4.02551 3.72267 4.0911 3.72267 4.16273V16.3289L5.02516 15.0406Z" fill="white" />
                    </g>
                  </svg>

                  {
                    (() => {
                      const newCount = unreadMessages && unreadMessages.length > 0 ? unreadMessages.length : 0;
                      const oldCount = userData && userData.unReadMessages ? userData.unReadMessages.length : 0;
                      const displayCount = newCount > 0 ? newCount : oldCount;
                      const shouldShow = newCount > 0 || oldCount > 0;
                      
                      console.log('Message count debug:', {
                        newCount,
                        oldCount,
                        displayCount,
                        shouldShow,
                        unreadMessages: unreadMessages?.length,
                        userDataUnReadMessages: userData?.unReadMessages?.length
                      });
                      
                      return shouldShow && (
                        <span className="text-[8px] font-bold rounded-full flex items-center justify-center bg-primary text-white absolute  w-[14px] h-[14px] right-[-.25rem]  bottom-[-.25rem]">
                          {displayCount}
                        </span>
                      );
                    })()
                  }
                </button>

                <button 
                  className="relative max-lg:hidden mr-4" 
                  onClick={() => {
                    console.log('=== NOTIFICATION BUTTON CLICKED ===');
                    console.log('Current showNotificationsBox state:', showNotificationsBox);
                    setShowNotificationsBox(true);
                    console.log('showNotificationsBox set to true');
                  }}
                  onDoubleClick={testAddNotification}
                  onContextMenu={debugNotificationCount}
                  onMouseEnter={debugNotifications}
                  onAuxClick={addNotificationsToExistingSalesTest}
                >
                  <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16ZM16 17H8V11C8 8.52 9.51 6.5 12 6.5C14.49 6.5 16 8.52 16 11V17Z" fill="white"/>
                  </svg>

                  {
                    unreadNotifications && unreadNotifications.length > 0 && (
                      <span className="text-[8px] font-bold rounded-full flex items-center justify-center bg-red-500 text-white absolute w-[14px] h-[14px] right-[-.25rem] bottom-[-.25rem]">
                        {unreadNotifications.length}
                      </span>
                    )
                  }
                </button>

                <button 
                  className="relative" 
                  onClick={() => setShowCart(true)}
                  onAuxClick={testEmailNotificationFunction}
                >
                  <span className="text-[8px] font-bold rounded-full flex items-center justify-center bg-primary text-white absolute  w-[14px] h-[14px] right-[-.25rem]  bottom-[-.25rem]">
                    {cartList ? cartList.length : 0}
                  </span>
                  <img src="/assets/icons/icon-bag.svg" alt="icon" />
                </button>
              </>
            }
          </div>
        </div>
      </div >
    </>
  )
}


export default Header
